<script lang="ts">
	// T30 Open shift (UC-60): one question, the cash a person counted into
	// the drawer. The box starts empty, never 0.00, so the float is always
	// typed; Confirm stays off until it is, and a typed 0 opens with 0.00.
	// The keypad fills from the right, as on Payment, and takes no key past
	// MAX_MINOR. Over Shift or Sell it is the only sheet; over Payment it is
	// the second, and Back returns to Payment.

	import Band from '../components/Band.svelte';
	import Field from '../components/Field.svelte';
	import Keypad from '../components/Keypad.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { formatMinor, parseTypedMinor } from '../format/money';
	import { message, type Language } from '../i18n/messages';
	import { loadShift, openShiftAttempt } from '../till/shift.svelte';
	import { typeTender } from './Payment.svelte';

	interface Props {
		open: boolean;
		language: Language;
		minorDigits: number;
		/** 2 over Payment, whose title is the Back button's. */
		level?: 1 | 2;
		parentTitle?: string;
		/**
		 * Told whether a float is typed, kept while the sheet is closed, or an
		 * open is on its way: the till's own lock installs no app update over
		 * it (#925).
		 */
		onHolding?: (holding: boolean) => void;
		onClose: () => void;
	}

	let { open, language, minorDigits, level = 1, parentTitle, onHolding, onClose }: Props = $props();

	let typed = $state('');
	let busy = $state(false);
	let slow = $state(false);
	let failure = $state('');
	let send = openShiftAttempt();

	$effect(() => {
		onHolding?.(typed !== '' || busy);
	});

	const floatMinor = $derived(parseTypedMinor(typed));

	function done() {
		typed = '';
		failure = '';
		send = openShiftAttempt();
		onClose();
	}

	async function confirm() {
		if (floatMinor === null || busy) return;
		busy = true;
		failure = '';
		const timer = setTimeout(() => (slow = true), 300);
		try {
			const { data, error } = await send({ floatMinor });
			if (error === undefined && data) {
				await loadShift().catch(() => undefined);
				done();
				return;
			}
			const code = error?.code;
			if (code === 'SHIFT_OPEN') {
				// Another tap or another user opened it: the screen beneath shows it.
				await loadShift().catch(() => undefined);
				failure = message(language, 'shiftAlreadyOpen');
			} else if (code === 'NOT_A_CASH_POINT') {
				failure = message(language, 'shiftNotCashPoint');
			} else if (code === 'VALIDATION_ERROR') {
				failure = message(language, 'shiftTooLarge');
			} else {
				failure = message(language, 'ordersFailed');
			}
		} catch {
			failure = message(language, 'offline');
		} finally {
			clearTimeout(timer);
			busy = false;
			slow = false;
		}
	}
</script>

<Sheet
	{open}
	{level}
	title={message(language, 'shiftOpen')}
	backLabel={parentTitle}
	onBack={level === 2 ? onClose : undefined}
	backTestId="open-shift-back"
	onClose={level === 1 ? onClose : undefined}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="open-shift-close"
	testId="panel-open-shift"
	primaryLabel={message(language, 'confirm')}
	primaryIcon="check"
	primaryTestId="open-shift-confirm"
	primaryDisabled={floatMinor === null || busy}
	primaryState={slow ? 'pending' : 'idle'}
	pendingLabel={message(language, 'shiftOpening')}
	onPrimary={() => void confirm()}
	locked={busy}
>
	<!-- The question and the box beside the keypad, as on Payment, so the
	     whole pad fits a 1024x600 till. -->
	<div class="open-shift">
		<div class="ask">
			<p class="question" data-testid="open-shift-question">
				{message(language, 'shiftOpenQuestion')}
			</p>
			{#if failure}
				<Band tone="flag" testId="open-shift-error">{failure}</Band>
			{/if}
			<div dir="ltr">
				<Field
					testId="open-shift-amount"
					label={message(language, 'shiftOpenAmount')}
					value={floatMinor === null ? '–' : formatMinor(floatMinor, minorDigits)}
					mono
				/>
			</div>
		</div>
		<div class="pad">
			<Keypad
				testId="open-shift-pad"
				label={message(language, 'shiftOpenAmount')}
				deleteLabel={message(language, 'backspace')}
				disabled={busy}
				canType={typeTender(typed, '0') !== null}
				canDelete={typed.length > 0}
				onKey={(key) => {
					const next = typeTender(typed, key);
					if (next !== null) typed = next;
				}}
				onBackspace={() => (typed = typed.slice(0, -1))}
			/>
		</div>
	</div>
</Sheet>

<style>
	.open-shift {
		display: flex;
		gap: var(--space-9);
	}

	.ask {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.pad {
		flex: none;
		align-self: flex-start;
	}

	.question {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
	}
</style>
